import { useState, useRef, useEffect } from 'react';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/PageSEO';
import { RegisterModal } from '../components/RegisterModal';
import { ToolFeedback } from '../components/ToolFeedback';
import { EmailVerificationWall } from '../components/EmailVerificationWall';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calculator, Loader2, ChevronRight, ChevronLeft, ChevronDown, DollarSign,
  Briefcase, Gift, CreditCard, Shield, CalendarDays, AlertTriangle, Check, Lock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas','Chihuahua',
  'Ciudad de México','Coahuila','Colima','Durango','Estado de México','Guanajuato',
  'Guerrero','Hidalgo','Jalisco','Michoacán','Morelos','Nayarit','Nuevo León','Oaxaca',
  'Puebla','Querétaro','Quintana Roo','San Luis Potosí','Sinaloa','Sonora','Tabasco',
  'Tamaulipas','Tlaxcala','Veracruz','Yucatán','Zacatecas',
];

const STEPS = [
  { num: 1, icon: DollarSign, label: 'Sueldo' },
  { num: 2, icon: Briefcase, label: 'Ingresos' },
  { num: 3, icon: Gift, label: 'Prestaciones' },
  { num: 4, icon: CreditCard, label: 'Deducciones' },
  { num: 5, icon: Shield, label: 'IMSS' },
  { num: 6, icon: CalendarDays, label: 'Antigüedad' },
];

const fmt = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v);
const pct = (v) => `${(v * 100).toFixed(2)}%`;

function NumInput({ label, value, onChange, help, placeholder, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <Input
        type="number"
        min="0"
        step="0.01"
        placeholder={placeholder || '0'}
        value={value || ''}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        data-testid={testId}
      />
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

export default function NetSalaryPage() {
  const { isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showBlurred, setShowBlurred] = useState(false);
  const [showDisclaimerDetail, setShowDisclaimerDetail] = useState(false);
  const [usageLimitReached, setUsageLimitReached] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [fiscalInfo, setFiscalInfo] = useState({ uma: { cuarenta_pct_mensual: 1426.49 } });
  const pendingRef = useRef(false);

  const [form, setForm] = useState({
    periodicity: 'mensual',
    gross_salary: '',
    days_worked: '',
    fiscal_year: 2026,
    state: '',
    commissions: 0, productivity_bonus: 0, punctuality_bonus: 0, attendance_bonus: 0,
    double_overtime_hours: 0, triple_overtime_hours: 0, other_income: 0, other_income_taxable: true,
    food_vouchers: 0, savings_fund_worker: 0, savings_fund_employer: 0, other_benefits: 0,
    infonavit: 0, fonacot: 0, child_support: 0, other_deductions: 0,
    knows_sbc: 'no', sbc: 0,
    start_date: '', aguinaldo_days: 15, vacation_days: 12, vacation_premium_pct: 25,
  });

  const set = (field, val) => setForm((p) => ({ ...p, [field]: val }));
  const defaultDays = { semanal: 7, catorcenal: 14, quincenal: 15, mensual: 30.4 };

  useEffect(() => {
    if (isAuthenticated) checkUsage();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchFiscalInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/fiscal/info`);
        if (res.ok) setFiscalInfo(await res.json());
      } catch {}
    };
    fetchFiscalInfo();
  }, []);

  const checkUsage = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/usage/status`);
      if (res.ok) {
        const data = await res.json();
        if (data.plan !== 'pro') {
          const ns = data.features?.net_salary;
          if (ns) setUsageLimitReached(ns.remaining <= 0);
        } else {
          setUsageLimitReached(false);
        }
      }
    } catch { /* non-blocking */ }
  };

  // Registra en usage_limit_events el momento exacto en que aparece el banner de límite
  // alcanzado. Depende de usageLimitReached (booleano), no se reenvía en cada checkUsage()
  // que vuelve a confirmar que sigue en el límite.
  useEffect(() => {
    if (usageLimitReached) {
      authFetch(`${API_URL}/api/usage-limit/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: 'net_salary', event: 'limit_reached' }),
      }).catch(() => { /* non-blocking */ });
    }
  }, [usageLimitReached]);

  const trackUpgradeClick = () => {
    authFetch(`${API_URL}/api/usage-limit/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool: 'net_salary', event: 'upgrade_click' }),
    }).catch(() => { /* non-blocking */ });
  };

  const computeResult = async () => {
    const payload = {
      ...form,
      gross_salary: parseFloat(form.gross_salary),
      days_worked: form.days_worked ? parseFloat(form.days_worked) : defaultDays[form.periodicity],
    };
    // authFetch attaches the auth token when present; the backend enforces the free-plan
    // limit for authenticated callers and allows an unlimited anonymous preview.
    const res = await authFetch(`${API_URL}/api/calculations/net-salary-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      if (res.status === 403) {
        setUsageLimitReached(true);
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Límite de cálculos alcanzado. Actualiza a Pro.');
      }
      throw new Error('Error al calcular');
    }
    return await res.json();
  };

  const handleCalculate = async () => {
    if (!form.gross_salary || parseFloat(form.gross_salary) <= 0) {
      toast.error('Ingresa tu sueldo bruto');
      return;
    }
    setLoading(true);
    try {
      const data = await computeResult();
      setResult(data);
      if (!isAuthenticated) {
        setShowBlurred(true);
        pendingRef.current = true;
        setShowRegisterModal(true);
      } else {
        checkUsage();
      }
    } catch (err) {
      toast.error(err.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSuccess = async (registeredUser) => {
    pendingRef.current = false;
    setShowBlurred(false);
    // Cuenta recién creada, aún sin verificar: el muro se mostrará en el siguiente
    // render en vez de recalcular con la cuenta ya autenticada.
    if (registeredUser?.email_verified === false) return;
    try {
      // Recompute now that the user is authenticated, so this calculation is
      // properly checked and counted against their new account's free-plan limit.
      const data = await computeResult();
      setResult(data);
    } catch (err) {
      toast.error(err.message || 'Error al calcular');
    }
    checkUsage();
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4" data-testid="step-1">
            <div className="space-y-1">
              <Label>Periodicidad de pago</Label>
              <Select value={form.periodicity} onValueChange={(v) => set('periodicity', v)}>
                <SelectTrigger data-testid="periodicity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="semanal">Semanal</SelectItem>
                  <SelectItem value="catorcenal">Catorcenal</SelectItem>
                  <SelectItem value="quincenal">Quincenal</SelectItem>
                  <SelectItem value="mensual">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <NumInput label="Sueldo base bruto por periodo" value={form.gross_salary}
              onChange={(v) => set('gross_salary', v)} testId="gross-salary-input"
              help="El sueldo bruto es tu salario antes de ISR, IMSS y otros descuentos." placeholder="Ej: 15000" />
            <NumInput label="Dias trabajados en el periodo" value={form.days_worked}
              onChange={(v) => set('days_worked', v)} testId="days-worked-input"
              help={`Si trabajaste el periodo completo, deja en blanco (${defaultDays[form.periodicity]} dias).`} />
            <div className="space-y-1">
              <Label>Ano fiscal</Label>
              <Select value={String(form.fiscal_year)} onValueChange={(v) => set('fiscal_year', parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="2026">2026</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Entidad federativa</Label>
              <Select value={form.state} onValueChange={(v) => set('state', v)}>
                <SelectTrigger data-testid="state-select"><SelectValue placeholder="Selecciona tu estado" /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4" data-testid="step-2">
            <p className="text-sm text-muted-foreground">Si no aplican, dejalo en 0.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumInput label="Comisiones" value={form.commissions} onChange={(v) => set('commissions', v)} />
              <NumInput label="Bono de productividad" value={form.productivity_bonus} onChange={(v) => set('productivity_bonus', v)} />
              <NumInput label="Bono de puntualidad" value={form.punctuality_bonus} onChange={(v) => set('punctuality_bonus', v)} />
              <NumInput label="Bono de asistencia" value={form.attendance_bonus} onChange={(v) => set('attendance_bonus', v)} />
              <NumInput label="Horas extra dobles" value={form.double_overtime_hours} onChange={(v) => set('double_overtime_hours', v)} help="Numero de horas" />
              <NumInput label="Horas extra triples" value={form.triple_overtime_hours} onChange={(v) => set('triple_overtime_hours', v)} help="Numero de horas" />
            </div>
            <NumInput label="Otros ingresos (monto)" value={form.other_income} onChange={(v) => set('other_income', v)} />
            <div className="flex items-center gap-2">
              <input type="checkbox" id="taxable" checked={form.other_income_taxable}
                onChange={(e) => set('other_income_taxable', e.target.checked)} className="rounded" />
              <Label htmlFor="taxable" className="text-sm">Este ingreso es gravado (si no sabes, dejalo marcado)</Label>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4" data-testid="step-3">
            <p className="text-sm text-muted-foreground">Si no aplican, dejalo en 0.</p>
            <NumInput label="Vales de despensa" value={form.food_vouchers} onChange={(v) => set('food_vouchers', v)}
              help={`Exentos hasta 40% de la UMA mensual (${fmt(fiscalInfo.uma.cuarenta_pct_mensual)} en 2026).`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumInput label="Fondo de ahorro (tu aportacion)" value={form.savings_fund_worker} onChange={(v) => set('savings_fund_worker', v)} />
              <NumInput label="Fondo de ahorro (aportacion empresa)" value={form.savings_fund_employer} onChange={(v) => set('savings_fund_employer', v)} />
            </div>
            <NumInput label="Otras prestaciones (monto)" value={form.other_benefits} onChange={(v) => set('other_benefits', v)} />
          </div>
        );
      case 4:
        return (
          <div className="space-y-4" data-testid="step-4">
            <p className="text-sm text-muted-foreground">Montos exactos descontados en tu recibo. Si no aplican, dejalo en 0.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumInput label="Infonavit" value={form.infonavit} onChange={(v) => set('infonavit', v)} help="Monto exacto descontado." />
              <NumInput label="Fonacot" value={form.fonacot} onChange={(v) => set('fonacot', v)} help="Monto exacto descontado." />
              <NumInput label="Pension alimenticia" value={form.child_support} onChange={(v) => set('child_support', v)} />
              <NumInput label="Otras deducciones" value={form.other_deductions} onChange={(v) => set('other_deductions', v)} />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-4" data-testid="step-5">
            <div className="space-y-1">
              <Label>Conoces tu Salario Base de Cotizacion (SBC) ante el IMSS?</Label>
              <Select value={form.knows_sbc} onValueChange={(v) => set('knows_sbc', v)}>
                <SelectTrigger data-testid="sbc-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="si">Si, lo conozco</SelectItem>
                  <SelectItem value="no">No lo conozco</SelectItem>
                  <SelectItem value="nosabe">No se que es</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.knows_sbc === 'si' && (
              <NumInput label="SBC diario" value={form.sbc} onChange={(v) => set('sbc', v)}
                help="Lo encuentras en tu constancia del IMSS o puedes pedirlo a RRHH." testId="sbc-input" />
            )}
            {form.knows_sbc !== 'si' && (
              <div className="p-4 bg-muted rounded-lg text-sm space-y-2" data-testid="sbc-help">
                <p className="font-medium text-[#0F172A]">Que es el SBC?</p>
                <p className="text-muted-foreground">
                  El Salario Base de Cotizacion es el salario diario que tu empresa reporta al IMSS.
                  Incluye tu sueldo diario mas un factor de integracion por aguinaldo y prima vacacional.
                  Si no lo conoces, lo estimaremos con base en tu sueldo y conceptos gravados.
                </p>
              </div>
            )}
          </div>
        );
      case 6:
        return (
          <div className="space-y-4" data-testid="step-6">
            <p className="text-sm text-muted-foreground">Estos datos se usan para la proyeccion anual.</p>
            <div className="space-y-1">
              <Label>Fecha de ingreso</Label>
              <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="start-date-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NumInput label="Dias de aguinaldo" value={form.aguinaldo_days} onChange={(v) => set('aguinaldo_days', v)}
                help="Minimo legal: 15 dias." />
              <NumInput label="Dias de vacaciones" value={form.vacation_days} onChange={(v) => set('vacation_days', v)}
                help="Minimo legal segun antiguedad: 12 dias (1 ano)." />
              <NumInput label="% Prima vacacional" value={form.vacation_premium_pct} onChange={(v) => set('vacation_premium_pct', v)}
                help="Minimo legal: 25%." />
            </div>
          </div>
        );
      default: return null;
    }
  };

  const renderResult = () => {
    if (!result) return null;
    const r = result.resumen;
    const isr = result.desglose_isr;
    const imss = result.desglose_imss;
    const ded = result.desglose_deducciones;
    const anual = result.proyeccion_anual;

    return (
      <div className="space-y-6" data-testid="net-salary-result">
        {/* Disclaimer */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900" data-testid="result-disclaimer">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
            <div className="flex-1">
              <p>Este es tu estimado — tu recibo real puede variar un poco según cómo tu empresa reporte tus datos al IMSS.</p>
              <button
                type="button"
                onClick={() => setShowDisclaimerDetail((prev) => !prev)}
                className="mt-2 flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 transition-colors"
                data-testid="result-disclaimer-toggle"
              >
                <span className="font-medium">¿Por qué puede variar?</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showDisclaimerDetail ? 'rotate-180' : ''}`} />
              </button>
              {showDisclaimerDetail && (
                <p className="mt-2 text-xs text-amber-800 leading-relaxed">
                  Puede variar por factores como el Salario Base de Cotización registrado ante el IMSS por tu empresa, conceptos gravados y exentos, subsidio al empleo, ajustes fiscales, incidencias de nómina, faltas, incapacidades, prestaciones, comisiones, bonos, deducciones, créditos, pensiones alimenticias y políticas internas del empleador.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`space-y-6 transition-all duration-500 ${showBlurred ? 'blur-md pointer-events-none select-none' : ''}`} data-testid="result-content">
          {/* Main Result */}
          <Card className="border-2 border-[#2563EB]/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Tu sueldo neto estimado ({result.periodo})</p>
                <p className="text-4xl font-bold text-[#0F172A] mt-1" data-testid="net-salary-amount">{fmt(r.sueldo_neto)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Breakdown */}
          <Card>
            <CardHeader><CardTitle className="text-base">Desglose del periodo</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Sueldo base bruto" value={r.sueldo_base_bruto} plus />
              {r.ingresos_adicionales > 0 && <Row label="Ingresos adicionales" value={r.ingresos_adicionales} plus />}
              {r.prestaciones > 0 && <Row label="Prestaciones" value={r.prestaciones} plus />}
              <div className="border-t pt-2"><Row label="Total percepciones" value={r.total_percepciones} bold /></div>
              <Row label="ISR" value={r.isr} minus />
              <Row label="IMSS" value={r.imss} minus />
              {r.otras_deducciones > 0 && <Row label="Otras deducciones" value={r.otras_deducciones} minus />}
              <div className="border-t pt-2"><Row label="Total retenciones" value={r.total_retenciones} bold minus /></div>
              <div className="border-t pt-2 border-[#2563EB]/30"><Row label="Sueldo neto" value={r.sueldo_neto} bold highlight /></div>
            </CardContent>
          </Card>

          {/* ISR Detail */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cómo se calculó tu ISR</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                México cobra el ISR por tramos: entre más ganas, más alta la tasa sobre lo que excede cada tramo. Así se aplicó en tu caso:
              </p>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Tu ingreso gravable" value={isr.base_gravable} />
              <Row label="Donde empieza tu tramo" value={isr.limite_inferior} />
              <Row label="Lo que ganas arriba de ese límite" value={isr.excedente} />
              <Row label={`Impuesto sobre ese excedente (${pct(isr.tasa_aplicada)})`} value={isr.impuesto_marginal} />
              <Row label="Cuota fija de tu tramo" value={isr.cuota_fija} note="Un monto que ya trae cada tramo, antes de sumarle el % marginal." />
              <div className="border-t pt-2"><Row label="ISR antes del subsidio" value={isr.isr_antes_subsidio} /></div>
              <Row label="Subsidio al empleo" value={isr.subsidio_empleo} plus note="Un descuento que da el gobierno si ganas poco." />
              <div className="border-t pt-2"><Row label="ISR final" value={isr.isr_final} bold /></div>
            </CardContent>
          </Card>

          {/* IMSS Detail */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detalle de IMSS</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Tu SBC (Salario Base de Cotización) es lo que tu empresa reporta al IMSS para calcular tus cuotas — no es lo mismo que tu sueldo bruto.
              </p>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="SBC diario" value={imss.sbc_diario} />
              <Row label={`Cuota IMSS sobre tu sueldo (${pct(imss.tasa_base)})`} value={imss.cuota_base} />
              {imss.cuota_excedente > 0 && (
                <Row
                  label="Cuota adicional (tu sueldo supera 3 UMA)"
                  value={imss.cuota_excedente}
                  note="La UMA es una medida que usa el gobierno para topes y cálculos — no tu sueldo mínimo."
                />
              )}
              <div className="border-t pt-2"><Row label="Total IMSS" value={imss.total} bold /></div>
            </CardContent>
          </Card>

          {/* Annual Projection */}
          <Card>
            <CardHeader><CardTitle className="text-base">Proyeccion anual</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label={`Sueldo neto x ${anual.periodos_por_ano} periodos`} value={anual.sueldo_neto_anual} />
              <Row label={`Aguinaldo bruto (${form.aguinaldo_days} dias)`} value={anual.aguinaldo_bruto} />
              <Row label="Prima vacacional bruta" value={anual.prima_vacacional_bruta} />
              <div className="border-t pt-2"><Row label="Ingreso bruto anual" value={anual.ingreso_bruto_anual} bold /></div>
            </CardContent>
          </Card>

          {/* Next step */}
          <Card className="border-[#2563EB]/20 bg-[#2563EB]/5">
            <CardContent className="p-4 text-sm">
              <p className="font-medium text-[#0F172A] mb-1">¿Y ahora qué?</p>
              <p className="text-muted-foreground">
                Guarda este número y compáralo con lo que te depositan. Si hay una diferencia grande, pregúntale a RRHH o{' '}
                <Link to="/advisor" className="text-[#2563EB] hover:underline font-medium">
                  llévale este desglose al Asesor IA
                </Link>{' '}
                para entender por qué.
              </p>
            </CardContent>
          </Card>
        </div>

        {!showBlurred && (
          <>
            <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="w-full" data-testid="recalculate-btn">
              Hacer otro calculo
            </Button>
            <ToolFeedback tool="calculadora_sueldo_neto" />
          </>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <PageSEO
        title="Calculadora de Sueldo Neto México | Klarvi"
        description="Calcula tu sueldo neto mensual en México: ISR, IMSS y deducciones incluidas. Descubre cuánto te queda libre de tu salario bruto, gratis."
        canonical="https://klarvi.org/net-salary"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Calculadora de Sueldo Neto - Klarvi",
          "url": "https://klarvi.org/net-salary",
          "description": "Calcula tu sueldo neto mensual en México a partir de tu salario bruto, incluyendo ISR, IMSS y deducciones.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "inLanguage": "es-MX",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "MXN" },
        }}
      />
      {isAuthenticated && user?.email_verified === false ? (
        <EmailVerificationWall />
      ) : (
      <>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-['Manrope']" data-testid="net-salary-title">
            Calculadora de Sueldo Neto
          </h1>
          <p className="text-muted-foreground mt-1">
            Descubre cuanto deberias recibir despues de impuestos, seguridad social y descuentos.
          </p>
        </div>

        {result ? renderResult() : (
          <Card>
            {/* Step Indicator */}
            <div className="flex border-b overflow-x-auto" data-testid="step-indicator">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const active = step === s.num;
                const done = step > s.num;
                return (
                  <button key={s.num} onClick={() => setStep(s.num)}
                    className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors
                      ${active ? 'border-[#2563EB] text-[#2563EB]' : done ? 'border-transparent text-[#0F172A]' : 'border-transparent text-muted-foreground'}`}
                    data-testid={`step-tab-${s.num}`}>
                    {done ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Icon className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{s.num}</span>
                  </button>
                );
              })}
            </div>

            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-4">
                Paso {step}: {STEPS[step - 1].label}
              </h2>
              {renderStep()}

              {/* Navigation */}
              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setStep(step - 1)} disabled={step === 1} data-testid="prev-step-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                {step < 6 ? (
                  <Button onClick={() => setStep(step + 1)} data-testid="next-step-btn">
                    Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : usageLimitReached ? (
                  <div className="text-center py-2 px-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2" data-testid="usage-limit-banner">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-800">Ya usaste tus 2 cálculos gratuitos de sueldo neto.</span>
                    <Link to="/precios" onClick={trackUpgradeClick}>
                      <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90 ml-2" data-testid="view-plans-btn">
                        Ver planes
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <Button onClick={handleCalculate} disabled={loading} className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="calculate-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                    Calcular sueldo neto
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <RegisterModal open={showRegisterModal} onOpenChange={(open) => { setShowRegisterModal(open); if (!open && showBlurred) { setShowBlurred(false); } }} onSuccess={handleRegisterSuccess} />
      </>
      )}
    </Layout>
  );
}

function Row({ label, value, bold, plus, minus, highlight, note }) {
  return (
    <div className={`flex justify-between items-start ${bold ? 'font-semibold' : ''} ${highlight ? 'text-[#2563EB]' : ''}`}>
      <span className="text-muted-foreground">
        {label}
        {note && <span className="block text-xs text-muted-foreground/70 font-normal mt-0.5">{note}</span>}
      </span>
      <span className={highlight ? 'text-lg' : ''}>
        {minus && value > 0 ? '-' : ''}{plus && value > 0 ? '+' : ''}{fmt(value)}
      </span>
    </div>
  );
}
